import type {
  ReviewUpdateSubscriptionPayloadSchema,
  SubscriptionSchema,
  UpdateDogSubscriptionActiveFeedingPlansPreviewOutput} from '@lyka-pet-food/contracts';
import {
  CancelReasonContext,
  CancelReasonSchema,
  DeliverySlotWindow,
  FeedingPlanServingSize,
  FeedingPlanType,
  Meal,
  ProductType,
  UpdateSubscriptionBodyExtraSchema,
  UpdateSubscriptionBodyFeedingPlanSkuSchema,
} from '@lyka-pet-food/contracts'
import type { Subscription } from '@repo/api-types'
import { initContract } from '@ts-rest/core'
import { z } from 'zod'
import { ErrorResponseSchema } from './general'

const c = initContract()

export const subscriptionContract = c.router(
  {
    cancelSubscription: {
      method: 'POST',
      path: '/subscriptions/:subscriptionId/cancel',
      pathParams: z.object({
        subscriptionId: z.string(),
      }),
      body: z.object({
        primaryCancelReasonId: z.string(),
        secondaryCancelReasonId: z.string().optional(),
        otherCancelReason: z.string().optional(),
      }),
      responses: {
        200: z.boolean(),
      },
    },
    getCancelReasons: {
      method: 'GET',
      path: '/cancel-reasons',
      query: z
        .object({
          context: z.nativeEnum(CancelReasonContext).optional(),
        })
        .optional(),
      responses: {
        200: z.array(CancelReasonSchema),
      },
    },
    getSubscriptions: {
      method: 'GET',
      path: '/subscriptions',
      responses: {
        200: c.type<{ subscriptions: Subscription[] }>(),
      },
    },
    /** @deprecated */
    updateSubscriptionExtras: {
      method: 'POST',
      path: '/subscriptions/:subscriptionId/update-extras',
      pathParams: z.object({
        subscriptionId: z.string(),
      }),
      body: z.array(UpdateSubscriptionBodyExtraSchema),
      responses: {
        200: c.type<{ subscription: Subscription }>(),
      },
    },

    addSubscriptionExtra: {
      method: 'POST',
      path: '/subscriptions/:subscriptionId/add-extra',
      pathParams: z.object({
        subscriptionId: z.string(),
      }),
      body: z.object({
        extraType: z.nativeEnum(ProductType),
        startOrderId: z.string(),
        nthOrder: z.number(),
        quantity: z.number(),
        productVariantId: z.string(),
      }),
      responses: {
        // TODO: send a more useful response
        200: c.type<{ success: boolean }>(),
      },
    },
    updateSubscriptionExtraQuantity: {
      method: 'POST',
      path: '/subscriptions/:subscriptionId/update-extra-quantity',
      pathParams: z.object({
        subscriptionId: z.string(),
      }),
      body: z.object({
        extraId: z.string(),
        quantity: z.number(),
      }),
      responses: {
        // TODO: send a more useful response
        200: c.type<{ success: boolean }>(),
      },
    },
    updateSubscriptionExtraFrequency: {
      method: 'POST',
      path: '/subscriptions/:subscriptionId/update-extra-frequency',
      pathParams: z.object({
        subscriptionId: z.string(),
      }),
      body: z.object({
        extraId: z.string(),
        startOrderId: z.string(),
        nthOrder: z.number(),
        quantity: z.number(),
        extraType: z.nativeEnum(ProductType),
        productVariantId: z.string(),
      }),
      responses: {
        // TODO: send a more useful response
        200: c.type<{ success: boolean }>(),
      },
    },
    deleteSubscriptionExtra: {
      method: 'POST',
      path: '/subscriptions/:subscriptionId/delete-extra',
      pathParams: z.object({
        subscriptionId: z.string(),
      }),
      body: z.object({
        extraId: z.string(),
        cancelReasonId: z.string().optional(),
        cancelNotes: z.string().optional(),
      }),
      responses: {
        // TODO: send a more useful response
        200: c.type<{ success: boolean }>(),
      },
    },

    updateSubscriptionRecipes: {
      method: 'POST',
      path: '/subscriptions/:subscriptionId/:feedingPlanId/update-recipes',
      pathParams: z.object({
        subscriptionId: z.string(),
        feedingPlanId: z.string(),
      }),
      body: z.array(UpdateSubscriptionBodyFeedingPlanSkuSchema),
      responses: {
        200: c.type<{ subscription: Subscription }>(),
      },
    },
    updateSubscriptionServingSize: {
      method: 'POST',
      path: '/subscriptions/update-serving-size',
      body: z.object({
        subscriptionId: z.string(),
        feedingPlanId: z.string(),
        servingSize: z.nativeEnum(FeedingPlanServingSize),
      }),
      responses: {
        200: c.type<{ subscription: Subscription }>(),
      },
    },
    updateSubscriptionFeedingPlanType: {
      method: 'POST',
      path: '/subscriptions/update-subscription-feeding-plan-type',
      body: z.object({
        subscriptionId: z.string(),
        feedingPlanType: z.nativeEnum(FeedingPlanType),
      }),
      responses: {
        200: c.type<{ subscription: Subscription }>(),
      },
    },
    reviewUpdateSubscriptionServingSize: {
      method: 'POST',
      path: '/review-update-subscription-serving-size',
      body: z.object({
        subscriptionId: z.string(),
        servingSize: z.nativeEnum(FeedingPlanServingSize),
        feedingPlanId: z.string(),
      }),
      responses: {
        200: c.type<{ data: z.infer<typeof ReviewUpdateSubscriptionPayloadSchema> }>(),
      },
    },
    reviewUpdateSubscriptionFeedingPlanType: {
      method: 'POST',
      path: '/review-update-subscription-feeding-plan-type',
      body: z.object({
        subscriptionId: z.string(),
        feedingPlanType: z.nativeEnum(FeedingPlanType),
      }),
      responses: {
        200: c.type<{ data: z.infer<typeof ReviewUpdateSubscriptionPayloadSchema> }>(),
      },
    },
    reactivateSubscription: {
      method: 'POST',
      path: '/reactivate-subscription',
      body: z.object({
        deliveryDate: z.string(),
        preferredDeliverySlotId: z.nativeEnum(DeliverySlotWindow),
      }),
      responses: {
        200: c.type<{ data: z.infer<typeof SubscriptionSchema> }>(),
      },
    },
    previewReactivateSubscription: {
      method: 'POST',
      path: '/reactivate-subscription/preview',
      body: z.object({
        deliveryDate: z.string(),
        preferredDeliverySlotId: z.nativeEnum(DeliverySlotWindow),
      }),
      responses: {
        200: c.type<{
          data: {
            totalPrice: number
            totalCharge: number
            campaignDiscountApplied: boolean
            discountPercentOff: number | null
          }
        }>(),
      },
    },
    resumeSubscription: {
      method: 'POST',
      path: '/resume-subscription',
      body: z.object({
        deliveryDate: z.string(),
        preferredDeliverySlotId: z.nativeEnum(DeliverySlotWindow),
      }),
      responses: {
        200: c.type<{ data: z.infer<typeof SubscriptionSchema> }>(),
      },
    },
    previewDogRecommendedFeedingPlan: {
      method: 'POST',
      path: '/dog/preview-recommended-feeding-plan',
      body: z.object({
        dogId: z.string(),
      }),
      responses: {
        200: c.type<{
          data: Pick<UpdateDogSubscriptionActiveFeedingPlansPreviewOutput, 'toUpdate' | 'current'>
        }>(),
      },
    },
    applyDogRecommendedFeedingPlan: {
      method: 'POST',
      path: '/dog/apply-recommended-feeding-plan',
      body: z.object({
        dogId: z.string(),
        servingSize: z.number().int().positive(),
        meals: z.array(
          z.object({
            type: z.nativeEnum(Meal),
            quantity: z.number().int().nonnegative(),
          }),
        ),
      }),
      responses: {
        200: c.type(),
      },
    },
  },
  {
    commonResponses: {
      400: ErrorResponseSchema,
      401: ErrorResponseSchema,
      404: ErrorResponseSchema,
      500: ErrorResponseSchema,
    },
  },
)
